import {
  analytics,
  isRestError,
  tryParseFloat,
  useCountry,
  useCustomer,
  useDepositAccount,
  useDepositAutogiro,
  useDepositBbanValidation,
} from '@resurs-internal/rb-app-api';
import {useQueryClient} from '@tanstack/react-query';
import {useNavigate} from '@tanstack/react-router';
import {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useAlertDialog} from 'components';
import {useTranslate} from 'hooks';
import {useDialog} from 'hooks/useDialog/useDialog';
import {createBankIDUrl} from 'services/createBankIDUrl';
import {createRedirectPath} from 'services/uri/createRedirectPath';
import {getUpcomingDate} from '../../utils/DepositeMonthlyDepositUtils';
import {MONTHLY_DEPOSIT_CALLBACK_PATH} from '../DepositAccountMonthlyDepositCallbackPage';

export type DepositAccountMonthlyDepositDialogState = {accountId: string};

export type MonthlyDepositFormValues = {
  amount: string;
  accountPrefix: string;
  accountNumber: string;
  day: number;
};

export const DEFAULT_WITHDRAWAL_DAY = 8;

export const MAX_SELECTABLE_DAY = 28;

export const clampToSelectableDay = (day: number) =>
  Math.min(Math.max(day, 1), MAX_SELECTABLE_DAY);

export const useMonthlyDepositForm = () => {
  const translate = useTranslate();
  const country = useCountry();
  const customer = useCustomer();
  const alert = useAlertDialog();
  const navigate = useNavigate();
  const client = useQueryClient();
  const dialog = useDialog<DepositAccountMonthlyDepositDialogState>(
    'deposit-monthly-deposit',
  );

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const selectedAccountId = dialog.state?.accountId;
  const account = useDepositAccount(selectedAccountId);
  const autogiro = useDepositAutogiro(selectedAccountId);
  const bbanValidation = useDepositBbanValidation();

  const autogiroData = autogiro.query.data;
  const isAutogiroRegistered = Boolean(autogiroData?.active);

  const formDefaults = useMemo<MonthlyDepositFormValues>(() => {
    if (!autogiroData?.active) {
      return {
        amount: '',
        accountPrefix: '',
        accountNumber: '',
        day: DEFAULT_WITHDRAWAL_DAY,
      };
    }

    return {
      amount: (autogiroData.withdrawalAmount?.amount ?? '').toString(),
      accountPrefix: autogiroData.accountPrefix ?? '',
      accountNumber: autogiroData.accountNumber ?? '',
      day: clampToSelectableDay(
        autogiroData.withdrawalDayOfMonth ?? DEFAULT_WITHDRAWAL_DAY,
      ),
    };
  }, [autogiroData]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    clearErrors,
    setValue,
    formState: {errors, isValid, isSubmitting},
  } = useForm<MonthlyDepositFormValues>({
    mode: 'all',
    shouldUnregister: true,
    defaultValues: formDefaults,
  });

  const amount = watch('amount');
  const day = watch('day') ?? formDefaults.day;

  useEffect(() => {
    if (!dialog.open) return;

    reset(formDefaults);
  }, [dialog.open, formDefaults, selectedAccountId, reset]);

  useEffect(() => {
    register('day');
  }, [register]);

  const repeatDay = day.toString();
  const upcomingDate = useMemo(
    () => getUpcomingDate(day, country.locale),
    [country.locale, day],
  );

  const autogiroFailure = autogiro.query.failureReason;
  const hasNoConsent =
    Boolean(autogiroFailure) &&
    isRestError(autogiroFailure) &&
    autogiroFailure.status === 404;
  const isAutogiroUnavailable =
    autogiro.query.failureCount > 0 && !hasNoConsent;
  const isAutogiroUnknown =
    autogiro.query.isPending && !hasNoConsent && !isAutogiroUnavailable;

  const isBusy =
    autogiro.register.isPending ||
    autogiro.edit.isPending ||
    bbanValidation.validate.isPending ||
    isAutogiroUnknown ||
    account.query.isFetching;

  const isBusyOrSubmitting = isBusy || isSubmitting;
  const isSubmitDisabled =
    isBusyOrSubmitting ||
    isAutogiroUnavailable ||
    !selectedAccountId ||
    !isValid;

  const handleError = () => {
    alert.error({
      title: translate('savings.deposit.monthly.error.title'),
      subtitle: translate('common.error.message'),
    });
  };

  const showSuccess = (
    title: string,
    numericAmount: number,
    withdrawalDay: number,
  ) => {
    alert.success({
      title,
      subtitle: translate('savings.deposit.monthly.success.body', [
        country.formatAmount({
          amount: numericAmount,
          currency: country.currency,
        }),
        getUpcomingDate(withdrawalDay, country.locale),
        withdrawalDay.toString(),
      ]),
    });
  };

  const handleFormSubmit = async (data: MonthlyDepositFormValues) => {
    if (!selectedAccountId) return;

    const amountValue = tryParseFloat(String(data.amount)) || 0;
    const dayValue = Number(data.day) || formDefaults.day;

    try {
      if (isAutogiroRegistered) {
        await autogiro.edit.mutateAsync({
          withdrawalAmount: {
            amount: amountValue,
            currency: country.currency,
          },
          withdrawalDayOfMonth: dayValue,
        });

        await autogiro.query.refetch();
        await client.invalidateQueries({queryKey: ['accounts', 'deposit']});
        dialog.onClose();
        showSuccess(
          translate('savings.deposit.monthly.success.edit-title'),
          amountValue,
          dayValue,
        );
        return;
      }

      const customerId = customer.info?.customerId;
      if (!customerId) {
        handleError();
        return;
      }

      const accountPrefix = (data.accountPrefix ?? '').trim();
      const accountNumber = (data.accountNumber ?? '').trim();

      try {
        await bbanValidation.validate.mutateAsync({
          accountPrefix,
          accountNumber,
        });
      } catch (error) {
        if (isRestError(error) && error.status === 400) {
          // The backend validates the clearing + account number as a single
          // BBAN and returns one generic error, so flag both fields; the
          // combined message is rendered once below the grouped inputs.
          setError('accountPrefix', {type: 'server'});
          setError('accountNumber', {type: 'server'});
          return;
        }
        handleError();
        return;
      }

      const isSEBankID = country.code === 'SE';
      const response = await autogiro.register.mutateAsync({
        customerId: customerId.toString(),
        accountPrefix,
        accountNumber,
        withdrawalAmount: {
          amount: amountValue,
          currency: country.currency,
        },
        withdrawalDayOfMonth: dayValue,
        signingContext: {
          locale: country.locale,
          onSuccessUri: isSEBankID
            ? undefined
            : createRedirectPath(MONTHLY_DEPOSIT_CALLBACK_PATH, {
                accountId: selectedAccountId,
                status: 'SUCCESSFUL',
                action: 'register',
                amount: amountValue.toString(),
                day: dayValue.toString(),
              }),
          onCancelledUri: isSEBankID
            ? undefined
            : createRedirectPath(MONTHLY_DEPOSIT_CALLBACK_PATH, {
                accountId: selectedAccountId,
                status: 'ERROR',
                action: 'register',
              }),
        },
      });

      if (isSEBankID) {
        dialog.onClose();
        navigate({
          to: createBankIDUrl({
            token: response.signatureId,
            redirect: MONTHLY_DEPOSIT_CALLBACK_PATH,
            accountId: selectedAccountId,
            action: 'register',
            amount: amountValue.toString(),
            day: dayValue.toString(),
          }),
        });
        return;
      }

      if (response.redirect) {
        window.location.href = response.redirect;
        return;
      }

      await autogiro.query.refetch();
      await client.invalidateQueries({queryKey: ['accounts', 'deposit']});
      dialog.onClose();
      showSuccess(
        translate('savings.deposit.monthly.success.title'),
        amountValue,
        dayValue,
      );
    } catch (error) {
      if (!isAutogiroRegistered && isRestError(error) && error.status === 400) {
        setError('accountNumber', {
          type: 'server',
          message: translate('account.input.se.error.empty'),
        });
        return;
      }
      handleError();
    }
  };

  const onSubmit = handleSubmit(handleFormSubmit);

  const handleClose = () => {
    reset(formDefaults);
    setCancelDialogOpen(false);
    dialog.onClose();
  };

  const setDay = (nextDay: number) => {
    setValue('day', nextDay, {shouldDirty: true, shouldValidate: true});
  };

  // The server flags both fields for one BBAN, so editing either clears it.
  const handleAccountFieldChange = () => {
    if (
      errors.accountPrefix?.type === 'server' ||
      errors.accountNumber?.type === 'server'
    ) {
      clearErrors(['accountPrefix', 'accountNumber']);
    }
  };

  const openCancelDialog = () => {
    analytics.click({
      name: 'deposit_account_monthly_deposit_close_action',
    });
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => setCancelDialogOpen(false);

  return {
    dialog,
    register,
    errors,
    selectedAccountId,
    isAutogiroRegistered,
    isAutogiroUnavailable,
    isBusy,
    isBusyOrSubmitting,
    isSubmitDisabled,
    amount,
    day,
    repeatDay,
    upcomingDate,
    setDay,
    handleAccountFieldChange,
    onSubmit,
    handleClose,
    cancelDialogOpen,
    openCancelDialog,
    closeCancelDialog,
  };
};
